import { API_BASE_URL } from "../../config";
// src/Components/MapTrack.js
import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import StoreMarkers from "./Components/StoreMarkers";
import VehicleMarkers from "./Components/VehicleMarkers";
import useFetchUserSeller from "../Utils/useFetchUserSeller";  // Import the new hook
import { assignCustomerToRoute } from "../Utils/assignCustomerRoute";
import { useFetchData } from "../Utils/useFetchData";
import Select from "react-select";
import VehicleTrackMarkers from "./Components/VehicleTrackMarkers";
import StoreTrackMarkers from "./Components/StoreTrackMarkers";
import CustomerLink from '../Components/CustomerLink';

const MapEvents = ({ mapCenter, setMapCenter, zoom, setZoom, centerAtCar, setZoomedIn }) => {
    const map = useMap();

    const prevMapCenter = React.useRef(mapCenter);
    const prevZoom = React.useRef(zoom);

    useEffect(() => {
        const isCenterChanged = !prevMapCenter.current || 
            Math.abs(prevMapCenter.current[0] - mapCenter[0]) > 0.000001 || 
            Math.abs(prevMapCenter.current[1] - mapCenter[1]) > 0.000001;
        const isZoomChanged = prevZoom.current !== zoom;

        if (isCenterChanged || isZoomChanged) {
            map.setView(mapCenter, zoom);
            prevMapCenter.current = mapCenter;
            prevZoom.current = zoom;
        }
    }, [mapCenter, zoom, map]);

    useEffect(() => {
        const handleMove = () => {
            if (centerAtCar) return; 
            const center = map.getCenter();
            const newCenter = [center.lat, center.lng];
            
            prevMapCenter.current = newCenter;
            setMapCenter(newCenter);
        };

        const handleZoom = () => {
            const currentZoom = map.getZoom();
            
            prevZoom.current = currentZoom;
            setZoom(currentZoom);
            setZoomedIn(currentZoom > 15);
        };

        map.on("moveend", handleMove);
        map.on("zoomend", handleZoom);

        return () => {
            map.off("moveend", handleMove);
            map.off("zoomend", handleZoom);
        };
    }, [map, centerAtCar, setMapCenter, setZoom, setZoomedIn]);

    return null;
};

const MapTrack = ({ handleLogout }) => {

    const playbackInterval = React.useRef(null);

    const defaultPosition = [15.325319, 101.970879];
    const defaultZoom = 19;

    const getSavedMapCenter = () => {
        try {
            const raw = localStorage.getItem("mapCenter");
            if (!raw) return defaultPosition;
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length === 2) {
                const lat = Number(parsed[0]);
                const lng = Number(parsed[1]);
                if (isFinite(lat) && isFinite(lng)) return [lat, lng];
            }
            if (parsed && typeof parsed === "object" && "lat" in parsed && "lng" in parsed) {
                const lat = Number(parsed.lat);
                const lng = Number(parsed.lng);
                if (isFinite(lat) && isFinite(lng)) return [lat, lng];
            }
        } catch (e) {
            // ignore and fall back
        }
        return defaultPosition;
    };

    const getSavedZoom = () => {
        try {
            const raw = localStorage.getItem("mapZoom");
            if (!raw) return defaultZoom;
            const parsed = JSON.parse(raw);
            const z = Number(parsed);
            return isFinite(z) ? z : defaultZoom;
        } catch (e) {
            return defaultZoom;
        }
    };

    const savedMapCenter = getSavedMapCenter();
    const savedZoom = getSavedZoom();
    const [error, setError] = useState("");

    const [mapCenter, setMapCenter] = useState(savedMapCenter);
    const [zoom, setZoom] = useState(savedZoom);
    const [selectedUserId, setSelectedUserId] = useState(""); // State to track selected user ID
    const [selectedDate, setSelectedDate] = useState(""); // New state for date selection
    const [startTime, setStartTime] = useState("00:00:00"); // New state for start time
    const [endTime, setEndTime] = useState("23:59:59"); // New state for end time
    const [storeLocations, setStoreLocations] = useState([]);
    const [allCustomers, setAllCustomers] = useState([]); // Store all customers fetched once
    const [vehicleLocations, setVehicleLocations] = useState([]);

    const [currentTime, setcurrentTime] = useState([]);

    const [markerPositions, setMarkerPositions] = useState({});
    const [originalPositions, setOriginalPositions] = useState({});
    const [zoomedIn, setZoomedIn] = useState(savedZoom >= 17);
    const [trackingData, setTrackingData] = useState(null); // State to store tracking data
    const [trackedCustomers, setTrackedCustomers] = useState(null); // State to store new delivery tracking data
    const [showCars, setShowCars] = useState(true);
    const [showStores, setShowStores] = useState(true);
    const [showRegular, setShowRegular] = useState(true);
    const [showWalkIn, setShowWalkIn] = useState(false);
    const [showPotential, setShowPotential] = useState(false);
    const [showAgent, setShowAgent] = useState(false);
    const [centerAtCar, setCenterAtCar] = useState(false); // New state for centering map at car
    const [showVisitedCustomers, setShowVisitedCustomers] = useState(false); // New state for hiding visited customers list

    const [timeIndex, setTimeIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);


    const { fetchData } = useFetchData(handleLogout);

    // Use the custom hook for fetching user IDs
    const userIds = useFetchUserSeller(handleLogout);

    // Function to fetch tracking data with date and user ID
    const fetchTrackingData = async (userId, date, sTime = "00:00:00", eTime = "23:59:59") => {
        if (!userId || !date) return; // Ensure both user and date are selected
        
        if (sTime > eTime) {
            setError("เวลาเริ่มต้องไม่มากกว่าเวลาสิ้นสุด");
            return;
        }
        setError("");
        
        try {
            // 1. Fetch GPS tracking data
            const gpsData = await fetchData(
                `${API_BASE_URL}/gps/api/v1/gps/user/${userId}/date-time?date=${date}&startTime=${sTime}&endTime=${eTime}`
            );

            // 2. Fetch Delivery Tracking with proximity check
            const deliveryData = await fetchData(
                `${API_BASE_URL}/gps/api/v1/gps/hv1/gps/user/${userId}/date-time?date=${date}&startTime=${sTime}&endTime=${eTime}`
            );

            if (gpsData) {
                // Map the new GPS response fields to match what VehicleTrackMarkers expects
                const vehLocations = gpsData.map(point => ({
                    ...point,
                    latitude: Number(point.latitude),
                    longitude: Number(point.longitude),
                    heading: Number(point.direction) || 0,
                    charge: Boolean(point.charging),
                    speed: Number(point.speed) || 0,
                    battery: Number(point.battery)
                })).sort((a, b) => new Date(`${date} ${a.time}`) - new Date(`${date} ${b.time}`));

                const combinedData = {
                    vehLocations: vehLocations,
                };

                console.log("Tracking Data:", combinedData);
                setTrackingData(combinedData);
                setTimeIndex(0); // Reset time index when new data is fetched
                if (vehLocations.length > 0) {
                    const firstLoc = vehLocations[0];
                    setcurrentTime(firstLoc.time);
                }
            } else {
                setTrackingData({ vehLocations: [] });
            }

            if (deliveryData) {
                console.log("Delivery Data:", deliveryData);
                // Categorize customers
                const categorized = [];
                
                const processList = (list, type) => {
                    if (Array.isArray(list)) {
                        list.forEach(c => {
                            const cid = c.customerUid || c.customerId || c.id;
                            
                            // Exclude WAC- customers
                            if (cid && cid.startsWith('WAC-')) {
                                return;
                            }

                            const loc = deliveryData.customerLocations && deliveryData.customerLocations[cid];
                            // Enrich with storeName and coordinates from allCustomers if missing
                            const customerFromAll = allCustomers.find(ac => ac.id === cid);
                            
                            const lat = loc ? Number(loc.latitude) : (customerFromAll ? Number(customerFromAll.latitude) : Number(c.latitude));
                            const lng = loc ? Number(loc.longitude) : (customerFromAll ? Number(customerFromAll.longitude) : Number(c.longitude));

                            categorized.push({
                                ...c,
                                id: cid,
                                trackingType: type,
                                latitude: lat,
                                longitude: lng,
                                storeName: c.customerName || c.storeName || (customerFromAll ? customerFromAll.storeName : "ร้านค้า"),
                                order: c.orderNumber || c.order
                            });
                        });
                    }
                };

                processList(deliveryData.customerWhoBuy, 'BUY');
                processList(deliveryData.customerWhoNotBuy, 'NOT_BUY');
                processList(deliveryData.customerWhoUserNotGo, 'NOT_VISITED');
                processList(deliveryData.customerActualTimeNull, 'TIME_NULL');

                // Deduplicate by ID, keeping the first occurrence (which follows our priority BUY > NOT_BUY > ...)
                const uniqueCategorized = [];
                const seenIds = new Set();
                categorized.forEach(c => {
                    if (!seenIds.has(c.id)) {
                        seenIds.add(c.id);
                        uniqueCategorized.push(c);
                    }
                });

                setTrackedCustomers(uniqueCategorized.filter(s => isFinite(s.latitude) && isFinite(s.longitude)));
            } else {
                setTrackedCustomers([]);
            }

        } catch (error) {
            console.error("Error fetching tracking data:", error);
            setError("Error fetching tracking data.");
        }
    };




    const handleDeleteStoreLocation = async (storeId) => {
        // Feature disabled: /api/Manager/ is no longer usable
        return;
        /*
        try {
            const response = await fetchData(
                `${API_BASE_URL}/api/Manager/saleLocations/${storeId}`,
                "DELETE"
            );

            if (response) {
                // Successfully remove the deleted store from state
                setStoreLocations((prevLocations) =>
                    prevLocations.filter((location) => location.id !== storeId)
                );
            }
        } catch (error) {
            console.error("Delete error:", error);
            setError("Error deleting store location");
        }
        */

    };

    const assignCustomerData = async (userId, storeId) => {
        try {
            const token = JSON.parse(localStorage.getItem("user") || "{}").token;
            if (!token) return;
            await assignCustomerToRoute(token, storeId, userId);
        } catch (error) {
            console.error("Assignment error:", error);
            setError("Error assigning customer data");
        }
    };

    const handlePlay = () => {
        if (isPlaying) {
            setIsPlaying(false);
            clearInterval(playbackInterval.current); // Clear existing interval
            playbackInterval.current = null;
            return;
        }

        if (trackingData && trackingData.vehLocations) {
            setIsPlaying(true);
            playbackInterval.current = setInterval(() => {
                setTimeIndex((prevIndex) => {
                    const nextIndex = prevIndex + 1;
                    if (nextIndex < trackingData.vehLocations.length) {
                        // Update map center and current time
                        const nextLocation = trackingData.vehLocations[nextIndex];
                        if (centerAtCar) {
                            setMapCenter([nextLocation.latitude, nextLocation.longitude]);
                        }
                        setcurrentTime(nextLocation.time);
                        return nextIndex;
                    } else {
                        clearInterval(playbackInterval.current); // Stop playback at the end
                        playbackInterval.current = null;
                        setIsPlaying(false);
                        return prevIndex;
                    }
                });
            }, 100); // Adjust interval speed for smoother playback
        }
    };

    const handleSliderChange = (e) => {
        const newIndex = parseInt(e.target.value, 10);
        setTimeIndex(newIndex);

        // Update map center and current time immediately
        if (trackingData && trackingData.vehLocations[newIndex]) {
            const selectedLocation = trackingData.vehLocations[newIndex];
            if (centerAtCar) {
                setMapCenter([selectedLocation.latitude, selectedLocation.longitude]);
            }
            setcurrentTime(selectedLocation.time);
        }
    };




    const handleMarkerDragEnd = (event, id) => {
        const { lat, lng } = event.target.getLatLng();
        setMarkerPositions((prevPositions) => ({
            ...prevPositions,
            [id]: {
                oldPosition: originalPositions[id],
                newPosition: [lat, lng],
            },
        }));
    };

    const resetStorePosition = (id) => {
        if (originalPositions[id]) {
            setMarkerPositions((prevPositions) => {
                const updatedPositions = { ...prevPositions };
                delete updatedPositions[id];
                return updatedPositions;
            });
        }
    };

    useEffect(() => {
        localStorage.setItem("mapCenter", JSON.stringify(mapCenter));
        localStorage.setItem("mapZoom", JSON.stringify(zoom));
    }, [mapCenter, zoom]);

;


    useEffect(() => {
        // Fetch ALL customers once (one-time) just like DashMap
        const fetchAllCustomersOnce = async () => {
            try {
                const payload = { type: 'ALL' };
                const resp = await fetchData(`${API_BASE_URL}/customer/api/customers/by-category`, "POST", payload);
                if (!resp) return;

                const regular = Array.isArray(resp.regular) ? resp.regular : [];
                const walkIn = Array.isArray(resp.walkIn) ? resp.walkIn : [];
                const potential = Array.isArray(resp.potential) ? resp.potential : [];
                const agent = Array.isArray(resp.agent) ? resp.agent : [];

                const normalized = []
                    .concat(
                        regular.map(c => ({ ...c, id: c.customerUid, customerType: 'REGULAR', storeName: c.storeName || "ร้านค้าทั่วไป", latitude: Number(c.latitude), longitude: Number(c.longitude) })),
                        walkIn.map(w => ({ ...w, id: w.walkInCustomerUid, customerType: 'WALK_IN', storeName: w.storeName || "Walk-in", latitude: Number(w.latitude), longitude: Number(w.longitude) })),
                        agent.map(a => ({ ...a, id: a.agentCustomerUid, customerType: 'AGENT', storeName: a.storeName || "ตัวแทน", latitude: Number(a.latitude), longitude: Number(a.longitude) })),
                        potential.map(p => ({ ...p, id: p.potentialCustomerUid, customerType: 'POTENTIAL', storeName: p.storeName || "ว่าที่ร้านค้า", latitude: Number(p.latitude), longitude: Number(p.longitude) }))
                    )
                    .filter(s => isFinite(s.latitude) && isFinite(s.longitude));
                
                setAllCustomers(normalized);
            } catch (error) {
                console.error("Error fetching all customers:", error);
            }
        };
        fetchAllCustomersOnce();
    }, []);

    const handleUserSelect = (e) => {
        const userId = e.target.value;
        setSelectedUserId(userId);
        if (userId === "") {
            setTrackingData(null); // Reset tracking data when "All Users" is selected
            setTrackedCustomers(null);
        }
    };

    const getFilteredStores = () => {
        if (trackedCustomers) {
            return trackedCustomers;
        }
        
        return allCustomers.filter((s) => {
            if (!showStores) return false;
            const cat = s.customerType;
            if (cat === 'AGENT' && !showAgent) return false;
            if (cat === 'REGULAR' && !showRegular) return false;
            if (cat === 'WALK_IN' && !showWalkIn) return false;
            if (cat === 'POTENTIAL' && !showPotential) return false;
            return true;
        });
    };

    const filteredStores = getFilteredStores();

    // Filter customers who were visited
    const visitedCustomers = trackedCustomers 
        ? trackedCustomers.filter(c => c.trackingType === 'BUY' || c.trackingType === 'NOT_BUY') 
        : [];

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "20px" }}>
                {/* User Selection Dropdown */}
                <div style={{ maxWidth: "20%" }}>
                    <label htmlFor="user-select" style={{ display: "block", marginBottom: "5px" }}>
                        สายที่แสดงข้อมูล:
                    </label>
                    <Select
                        id="user-select"
                        options={[
                            { value: "", label: "เลือกสายที่จะแสดงข้อมูล" }, // Default option for all users
                            ...userIds.map((user) => ({
                                value: user.uid,
                                label: `${user.nameuser} (${user.username})`,
                            })),
                        ]}
                        value={
                            selectedUserId
                                ? {
                                    value: selectedUserId,
                                    label:
                                        userIds.find((user) => user.uid === selectedUserId)?.nameuser +
                                        " (" +
                                        userIds.find((user) => user.uid === selectedUserId)?.username +
                                        ")",
                                }
                                : { value: "", label: "แสดงทุกสาย" }
                        }
                        onChange={(selectedOption) => {
                            const userId = selectedOption ? selectedOption.value : "";
                            setSelectedUserId(userId);
                            if (userId === "") {
                                setTrackingData(null);
                                setTrackedCustomers(null);
                            }
                        }}
                        formatOptionLabel={(option) => (
                            <div>
                                <span style={{ fontWeight: "bold" }}>{option.label}</span>
                            </div>
                        )}
                        className="btc-selectBox-container"
                        classNamePrefix="btc-selectBox"
                        isClearable
                    />
                </div>

                {/* Date Selection */}
                <div style={{ maxWidth: "20%" }}>
                    <label htmlFor="date-select" style={{ display: "block", marginBottom: "5px" }}>
                        เลือกวันที่:
                    </label>
                    <input
                        id="date-select"
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        style={{ width: "100%", padding: "5px", fontSize: "14px" }}
                    />
                </div>

                {/* Start Time Selection */}
                <div style={{ maxWidth: "15%" }}>
                    <label htmlFor="start-time" style={{ display: "block", marginBottom: "5px" }}>
                        เวลาเริ่ม:
                    </label>
                    <input
                        id="start-time"
                        type="time"
                        step="1"
                        value={startTime}
                        onChange={(e) => {
                            const newStart = e.target.value;
                            setStartTime(newStart);
                            if (newStart > endTime) {
                                setEndTime(newStart);
                            }
                        }}
                        style={{ width: "100%", padding: "5px", fontSize: "14px" }}
                    />
                </div>

                {/* End Time Selection */}
                <div style={{ maxWidth: "15%" }}>
                    <label htmlFor="end-time" style={{ display: "block", marginBottom: "5px" }}>
                        เวลาสิ้นสุด:
                    </label>
                    <input
                        id="end-time"
                        type="time"
                        step="1"
                        value={endTime}
                        onChange={(e) => {
                            const newEnd = e.target.value;
                            setEndTime(newEnd);
                            if (newEnd < startTime) {
                                setStartTime(newEnd);
                            }
                        }}
                        style={{ width: "100%", padding: "5px", fontSize: "14px" }}
                    />
                </div>

                {/* Confirm Button */}
                <div>
                    <button
                        onClick={() => fetchTrackingData(selectedUserId, selectedDate, startTime, endTime)}
                        disabled={!selectedUserId || !selectedDate}
                        style={{
                            padding: "10px 20px",
                            fontSize: "16px",
                            backgroundColor: selectedUserId && selectedDate ? "#007BFF" : "#CCC",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            cursor: selectedUserId && selectedDate ? "pointer" : "not-allowed",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontWeight: "500"
                        }}
                    >
                        <i className="fas fa-route"></i>
                        ประมวลผลเส้นทาง
                    </button>
                </div>
            </div>

            {/* Selection Options similar to DashMap */}
            <div style={{ marginBottom: "10px", display: "flex", gap: "20px", alignItems: "center", paddingLeft: "20px" }}>
                {!trackedCustomers && (
                    <>
                        <label>
                            <input
                                type="checkbox"
                                checked={showCars}
                                onChange={(e) => setShowCars(e.target.checked)}
                            />
                            แสดงรถ
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                checked={showStores}
                                onChange={(e) => setShowStores(e.target.checked)}
                            />
                            แสดงร้านค้า
                        </label>
                        {showStores && (
                            <div style={{ display: 'flex', gap: '10px', marginLeft: '10px' }}>
                                <label>
                                    <input type="checkbox" checked={showRegular} onChange={(e) => setShowRegular(e.target.checked)} /> ปกติ
                                </label>
                                <label>
                                    <input type="checkbox" checked={showWalkIn} onChange={(e) => setShowWalkIn(e.target.checked)} /> Walk-in
                                </label>
                                <label>
                                    <input type="checkbox" checked={showPotential} onChange={(e) => setShowPotential(e.target.checked)} /> ว่าที่ร้านค้า
                                </label>
                                <label>
                                    <input type="checkbox" checked={showAgent} onChange={(e) => setShowAgent(e.target.checked)} /> ตัวแทน
                                </label>
                            </div>
                        )}
                        <label style={{ display: "flex", alignItems: "center", gap: "5px", cursor: "pointer", marginLeft: "10px", fontSize: "14px" }}>
                            <input
                                type="checkbox"
                                checked={centerAtCar}
                                onChange={(e) => setCenterAtCar(e.target.checked)}
                            />
                            ล็อคกึ่งกลางที่รถ
                        </label>
                    </>
                )}
                {trackedCustomers && (
                    <div style={{ fontWeight: 'bold', color: '#007BFF' }}>
                        กำลังแสดงข้อมูลการเข้าพบลูกค้า (Proximity Check)
                        <button 
                            onClick={() => setTrackedCustomers(null)}
                            style={{ 
                                marginLeft: '15px', 
                                padding: '4px 12px', 
                                fontSize: '12px', 
                                cursor: 'pointer',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                color: '#333',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px'
                            }}
                        >
                            <i className="fas fa-store"></i>
                            แสดงร้านค้าทั้งหมด
                        </button>
                    </div>
                )}
            </div>

            {error && (
                <div style={{ color: "red", paddingLeft: "20px", marginBottom: "10px" }}>
                    {error}
                </div>
            )}

            <div style={{ display: "flex", gap: "10px", padding: "0 20px" }}>
                <div style={{ flex: 1 }}>
                    <MapContainer center={mapCenter} key="dashmap" zoom={zoom}
                                  style={{height: "650px", width: "100%", zIndex: "15"}}>
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        <MapEvents
                            mapCenter={mapCenter}
                            setMapCenter={setMapCenter}
                            zoom={zoom}
                            setZoom={setZoom}
                            centerAtCar={centerAtCar}
                            setZoomedIn={setZoomedIn}
                        />
                        {/* Display store and vehicle locations based on tracking data */}
                        {filteredStores.length > 0 && (
                            <StoreTrackMarkers
                                storeLocations={filteredStores}
                                markerPositions={markerPositions}
                                zoomedIn={zoomedIn}
                                handleMarkerDragEnd={handleMarkerDragEnd}
                                handleResetStorePosition={resetStorePosition}
                                handleDeleteStoreLocation={handleDeleteStoreLocation}
                                currenttime={currentTime}
                            />
                        )}
                        {showCars && trackingData && trackingData.vehLocations && trackingData.vehLocations.length > 0 ? (
                            <VehicleTrackMarkers
                                vehicleLocations={trackingData.vehLocations}
                                timeIndex={timeIndex}
                                isPlaying={isPlaying}
                                zoomedIn={zoomedIn}
                            />
                        ) : null}
                    </MapContainer>
                </div>

                {trackedCustomers && (
                    <div style={{
                        width: showVisitedCustomers ? "300px" : "40px",
                        height: "650px",
                        overflowY: "auto",
                        border: "1px solid #ccc",
                        borderRadius: "5px",
                        padding: showVisitedCustomers ? "10px" : "5px",
                        backgroundColor: "#f9f9f9",
                        transition: "width 0.3s ease",
                        position: "relative"
                    }}>
                        <button 
                            onClick={() => setShowVisitedCustomers(!showVisitedCustomers)}
                            style={{
                                width: "100%",
                                padding: "8px 0",
                                marginBottom: showVisitedCustomers ? "10px" : "0",
                                cursor: "pointer",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                fontSize: "14px",
                                color: "#555"
                            }}
                            title={showVisitedCustomers ? "ซ่อนรายชื่อ" : "แสดงรายชื่อ"}
                        >
                            <i className={`fas ${showVisitedCustomers ? 'fa-times' : 'fa-bars'}`}></i>
                        </button>

                        {showVisitedCustomers && (
                            <>
                                <h4 style={{ marginTop: 0, borderBottom: "1px solid #ddd", paddingBottom: "5px", fontSize: "16px" }}>
                                    ลูกค้าที่เข้าพบ ({visitedCustomers.length})
                                </h4>
                                {visitedCustomers.length === 0 ? (
                                    <p style={{ color: "#666", fontSize: "14px" }}>ไม่มีลูกค้าที่เข้าพบ</p>
                                ) : (
                                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                        {visitedCustomers.map((customer) => (
                                            <div 
                                                key={customer.id} 
                                                style={{
                                                    padding: "10px",
                                                    backgroundColor: "white",
                                                    borderRadius: "4px",
                                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                                    borderLeft: customer.buy ? "4px solid #46af38" : "4px solid #ffc107",
                                                    cursor: "pointer"
                                                }}
                                                onClick={() => {
                                                    setMapCenter([customer.latitude, customer.longitude]);
                                                }}
                                            >
                                                <div style={{ fontWeight: "bold", fontSize: "14px", marginBottom: "3px" }}>
                                                    {customer.order}. <CustomerLink name={customer.storeName} code={customer.id} type={customer.customerType} onClick={(e) => e.stopPropagation()} />
                                                </div>
                                                <div style={{ fontSize: "12px", color: customer.buy ? "#46af38" : "#856404", fontWeight: "500", marginTop: "5px" }}>
                                                    สถานะ: {customer.status || (customer.buy ? 'ซื้อ' : 'ไม่ซื้อ')}
                                                </div>
                                                {(customer.actualTime || customer.visitTime) && (
                                                    <div style={{ fontSize: "11px", color: "#999", marginTop: "3px" }}>
                                                        เวลาเข้าพบ: {customer.actualTime || customer.visitTime}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {trackingData && trackingData.vehLocations && trackingData.vehLocations.length > 0 && (
                <div style={{ marginTop: "10px", padding: "0 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <button onClick={handlePlay} style={{ 
                            padding: "8px 16px",
                            backgroundColor: isPlaying ? "#dc3545" : "#46af38",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                            minWidth: "100px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            fontWeight: "500"
                        }}>
                            <i className={`fas ${isPlaying ? 'fa-pause' : 'fa-play'}`}></i>
                            {isPlaying ? "หยุด" : "เล่น"}
                        </button>
                        <input
                            type="range"
                            min="0"
                            max={trackingData.vehLocations.length - 1}
                            value={timeIndex}
                            onChange={handleSliderChange}
                            style={{ flexGrow: 1, cursor: "pointer" }}
                        />
                        <span style={{ fontWeight: "bold", minWidth: "150px" }}>
                            {timeIndex + 1} / {trackingData.vehLocations.length} ({currentTime})
                        </span>
                    </div>
                </div>
            )}



        </div>


    );


};

export default MapTrack;
